import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, Prompt, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from '@/common/components/Loader/index';
import Modal from '@/common/components/Modal/index';
import { Notification, formatDisplayDate } from '@/common/utils/index';
import { routes } from '@/app/routing/index';
import DeleteReseller from '@/modules/admin-reseller-form/DeleteReseller';
import ResellerFormFields, {
  DEFAULT_RESELLER_VALUES,
} from '@/modules/admin-reseller-form/ResellerFormFields';
import { useLocale } from '@/common/hooks/index';
import useLogoutIntentStore from '@/common/stores/logout-intent';
import styles from '@/modules/admin-reseller-form/styles.less';
import promptStyles from '@/common/containers/Modals/PromptSaveModal/styles.less';
import {
  useAdminResellerQuery,
  useCreateResellerMutation,
  useUpdateResellerMutation,
} from '@/entities/admin-resellers';

const FORM_ID = 'admin-reseller-form';

const AdminResellerFormModule = () => {
  const { t } = useTranslation();
  const { isFr } = useLocale();
  const history = useHistory();
  const { id } = useParams();
  const pendingLogoutPath = useLogoutIntentStore((state) => state.pendingPath);
  const clearLogout = useLogoutIntentStore((state) => state.clearLogout);
  const [formState, setFormState] = useState({});
  const [backModalOpen, setBackModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [navigationConfirmed, setNavigationConfirmed] = useState(false);
  const isEditMode = Boolean(id);

  const { data, isLoading } = useAdminResellerQuery(id, {
    enabled: isEditMode,
  });

  const createMutation = useCreateResellerMutation({
    onSuccess: () => {
      Notification.success(t('Reseller created successfully'));
      history.push(routes.adminPanel.resellers);
    },
    onError: () => {
      Notification.error(t('Failed to create reseller'));
    },
  });
  const updateMutation = useUpdateResellerMutation(id, {
    onSuccess: () => {
      Notification.success(t('Reseller updated successfully'));
      history.push(routes.adminPanel.resellers);
    },
    onError: () => {
      Notification.error(t('Failed to update reseller'));
    },
  });
  const mutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = useCallback(
    async (form) => {
      await mutation.mutateAsync({
        ...form,
        phone: form.phone.trim(),
      });
    },
    [mutation]
  );

  const onFormStateChange = useCallback((nextState) => {
    setFormState(nextState);
  }, []);

  useEffect(() => {
    if (!formState.isDirty || mutation.isPending) {
      window.onbeforeunload = null;
      return undefined;
    }

    window.onbeforeunload = () => true;

    return () => {
      window.onbeforeunload = null;
    };
  }, [formState.isDirty, mutation.isPending]);

  const onBlockedNavigation = useCallback(
    (nextLocation, action) => {
      if (!formState.isDirty || mutation.isPending || navigationConfirmed) {
        return true;
      }

      setPendingNavigation({ nextLocation, action });
      setBackModalOpen(true);
      return false;
    },
    [formState.isDirty, mutation.isPending, navigationConfirmed]
  );

  const leaveWithoutSaving = useCallback(() => {
    setBackModalOpen(false);
    setNavigationConfirmed(true);
  }, []);

  const continueEditing = useCallback(() => {
    const nextPath = pendingNavigation?.nextLocation?.pathname;

    if (pendingLogoutPath && nextPath === pendingLogoutPath) {
      clearLogout();
    }

    setBackModalOpen(false);
    setPendingNavigation(null);
  }, [clearLogout, pendingLogoutPath, pendingNavigation]);

  useEffect(() => {
    if (!navigationConfirmed || !pendingNavigation) return;

    const { nextLocation, action } = pendingNavigation;
    const nextPath = `${nextLocation.pathname || ''}${
      nextLocation.search || ''
    }${nextLocation.hash || ''}`;

    setNavigationConfirmed(false);
    setPendingNavigation(null);

    if (action === 'REPLACE') {
      history.replace(nextPath);
      return;
    }

    history.push(nextPath);
  }, [history, navigationConfirmed, pendingNavigation]);

  const initialValues = useMemo(
    () => ({
      ...DEFAULT_RESELLER_VALUES,
      ...(data?.data ?? {}),
    }),
    [data]
  );
  const reseller = data?.data;
  const createdAt = reseller?.createdAt
    ? formatDisplayDate(reseller.createdAt, isFr ? 'fr' : 'en')
    : null;

  const headerTitle = isEditMode
    ? reseller?.name || t('Edit Reseller')
    : t('New Reseller');
  const showIncompleteFormPrompt = formState.isDirty && !formState.isValid;

  if (isLoading) {
    return (
      <section className={styles.page}>
        <Link className={styles.linkBack} to={routes.adminPanel.resellers}>
          <ArrowBackIcon /> {t('Back to all resellers')}
        </Link>
        <div className={styles.header}>
          <h3>{t('Edit Reseller')}</h3>
        </div>
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <Prompt
        when={!!formState.isDirty && !mutation.isPending}
        message={onBlockedNavigation}
      />
      <Link className={styles.linkBack} to={routes.adminPanel.resellers}>
        <ArrowBackIcon /> {t('Back to all resellers')}
      </Link>
      <div className={styles.header}>
        <h3>{headerTitle}</h3>
        {isEditMode && createdAt && (
          <p className={styles.headerDate}>
            {t('Created on')}: {createdAt}
          </p>
        )}
      </div>
      <div className={styles.content}>
        <ResellerFormFields
          id={FORM_ID}
          className={styles.form}
          formId={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
          onFormStateChange={onFormStateChange}
        />
      </div>
      <Modal className={promptStyles.modal} open={backModalOpen} overlay>
        <h4 className={promptStyles.modalTitle}>
          {showIncompleteFormPrompt
            ? t('Incomplete form')
            : t('Leave this page?')}
        </h4>
        <p className={promptStyles.modalDescription}>
          {showIncompleteFormPrompt
            ? t(
                'You have unsaved changes, but some required fields are missing. Complete the form to save.'
              )
            : t('Unsaved changes will be lost if you leave this page.')}
        </p>
        <div className={promptStyles.modalBtns}>
          <Button variant="contained" color="primary" onClick={continueEditing}>
            {showIncompleteFormPrompt ? t('Continue editing') : t('Cancel')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={leaveWithoutSaving}
          >
            {showIncompleteFormPrompt ? t('Discard changes') : t('Leave page')}
          </Button>
        </div>
      </Modal>
      <div className={styles.footer}>
        {isEditMode && (
          <DeleteReseller resellerId={id} disabled={mutation.isPending} />
        )}
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={
            !formState.isDirty || !formState.isValid || mutation.isPending
          }
          loading={mutation.isPending}
          sx={{ width: 'fit-content', marginLeft: 'auto' }}
        >
          {t(isEditMode ? 'Save updates' : 'Create resellers')}
        </Button>
      </div>
    </section>
  );
};

export default AdminResellerFormModule;
